// src/pages/finance-portal/credit-memos/CreditMemoForm.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card, CardContent,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Alert, CircularProgress, Autocomplete, Divider,
    MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const REFUND_METHODS = [
    { value: 'ORIGINAL_PAYMENT', label: 'Original Payment' },
    { value: 'ACCOUNT_CREDIT', label: 'Account Credit' },
    { value: 'MANUAL_CHECK', label: 'Manual Check' },
    { value: 'NONE', label: 'None' },
];

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

const emptyLine = {
    description: '',
    quantity: 1,
    unit_price: 0,
    tax_amount: 0,
};

export default function CreditMemoForm() {
    const navigate = useNavigate();
    const { memoId } = useParams();
    const isEdit = Boolean(memoId);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [errors, setErrors] = useState({});
    const [success, setSuccess] = useState(null);

    // Customer
    const [customerOptions, setCustomerOptions] = useState([]);
    const [customerSearch, setCustomerSearch] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerLoading, setCustomerLoading] = useState(false);
    const customerTimer = useRef(null);

    // Original invoice
    const [invoiceOptions, setInvoiceOptions] = useState([]);
    const [invoiceSearch, setInvoiceSearch] = useState('');
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const invoiceTimer = useRef(null);

    // Form fields
    const [creditDate, setCreditDate] = useState(new Date().toISOString().split('T')[0]);
    const [refundMethod, setRefundMethod] = useState('NONE');
    const [reason, setReason] = useState('');
    const [notes, setNotes] = useState('');

    // Line items
    const [lines, setLines] = useState([{ ...emptyLine }]);

    // Customer search with debounce
    useEffect(() => {
        if (customerTimer.current) clearTimeout(customerTimer.current);
        const params = { per_page: 50 };
        if (customerSearch && customerSearch.length >= 2) params.search = customerSearch;
        setCustomerLoading(true);
        customerTimer.current = setTimeout(async () => {
            try {
                const res = await axiosInstance.get('/api/finance/customers', { params });
                if (res.data.success) {
                    setCustomerOptions(res.data.data || []);
                }
            } catch (err) {
                console.error('Failed to search customers', err);
            } finally {
                setCustomerLoading(false);
            }
        }, customerSearch ? 300 : 0);
        return () => { if (customerTimer.current) clearTimeout(customerTimer.current); };
    }, [customerSearch]);

    // Invoice search with debounce (filtered by selected customer)
    useEffect(() => {
        if (invoiceTimer.current) clearTimeout(invoiceTimer.current);
        if (!selectedCustomer) {
            setInvoiceOptions([]);
            return;
        }
        setInvoiceLoading(true);
        invoiceTimer.current = setTimeout(async () => {
            try {
                const params = { customer_id: selectedCustomer.id };
                if (invoiceSearch) params.search = invoiceSearch;
                const res = await axiosInstance.get('/api/finance/invoices', { params });
                if (res.data.success) {
                    setInvoiceOptions(res.data.data || []);
                }
            } catch (err) {
                console.error('Failed to search invoices', err);
            } finally {
                setInvoiceLoading(false);
            }
        }, 300);
        return () => { if (invoiceTimer.current) clearTimeout(invoiceTimer.current); };
    }, [selectedCustomer, invoiceSearch]);

    // When customer changes, clear selected invoice
    useEffect(() => {
        setSelectedInvoice(null);
        setInvoiceOptions([]);
        setInvoiceSearch('');
    }, [selectedCustomer]);

    // When original invoice is selected, auto-populate line items from that invoice
    const handleInvoiceSelect = async (invoice) => {
        setSelectedInvoice(invoice);
        if (!invoice) return;

        try {
            const res = await axiosInstance.get(`/api/finance/invoices/${invoice.id}`);
            if (res.data.success && res.data.data?.line_items) {
                const invoiceLines = res.data.data.line_items;
                if (invoiceLines.length > 0) {
                    setLines(invoiceLines.map(li => ({
                        description: li.description || '',
                        quantity: li.quantity != null ? li.quantity : 1,
                        unit_price: li.unit_price != null ? li.unit_price : 0,
                        tax_amount: li.tax_amount != null ? li.tax_amount : ((parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0) * (parseFloat(li.tax_rate) || 0) / 100),
                    })));
                }
            }
        } catch (err) {
            console.error('Failed to load invoice items', err);
        }
    };

    // Load existing credit memo for edit
    useEffect(() => {
        if (isEdit) {
            setLoading(true);
            axiosInstance.get(`/api/finance/credit-memos/${memoId}`)
                .then(res => {
                    if (res.data.success) {
                        const cm = res.data.data;
                        setCreditDate(cm.credit_date || new Date().toISOString().split('T')[0]);
                        setRefundMethod(cm.refund_method || 'NONE');
                        setReason(cm.reason || '');
                        setNotes(cm.notes || '');

                        if (cm.customer) {
                            setSelectedCustomer(cm.customer);
                            setCustomerOptions([cm.customer]);
                        }

                        if (cm.original_invoice) {
                            setSelectedInvoice(cm.original_invoice);
                            setInvoiceOptions([cm.original_invoice]);
                        }

                        if (cm.line_items && cm.line_items.length > 0) {
                            setLines(cm.line_items.map(li => ({
                                id: li.id,
                                description: li.description || '',
                                quantity: li.quantity != null ? li.quantity : 1,
                                unit_price: li.unit_price != null ? li.unit_price : 0,
                                tax_amount: li.tax_amount != null ? li.tax_amount : 0,
                            })));
                        }
                    }
                })
                .catch(err => {
                    setError(err.response?.data?.message || 'Failed to load credit memo');
                })
                .finally(() => setLoading(false));
        }
    }, [isEdit, memoId]);

    const updateLine = (index, field, value) => {
        const updated = [...lines];
        updated[index] = { ...updated[index], [field]: value };
        setLines(updated);
    };

    const addLine = () => {
        setLines([...lines, { ...emptyLine }]);
    };

    const removeLine = (index) => {
        if (lines.length > 1) {
            setLines(lines.filter((_, i) => i !== index));
        }
    };

    // Calculations
    const lineAmount = (line) => {
        return (parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price) || 0);
    };

    const subtotal = lines.reduce((sum, l) => sum + lineAmount(l), 0);
    const totalTax = lines.reduce((sum, l) => sum + (parseFloat(l.tax_amount) || 0), 0);
    const total = subtotal + totalTax;

    const buildPayload = () => {
        return {
            customer_id: selectedCustomer?.id || null,
            original_invoice_id: selectedInvoice?.id || null,
            credit_date: creditDate,
            refund_method: refundMethod,
            reason: reason,
            notes: notes,
            status: 'DRAFT',
            items: lines.map((l) => ({
                id: l.id || undefined,
                description: l.description,
                quantity: parseFloat(l.quantity) || 0,
                unit_price: parseFloat(l.unit_price) || 0,
                tax_amount: parseFloat(l.tax_amount) || 0,
            })),
        };
    };

    const handleSaveDraft = async () => {
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const payload = buildPayload();

            if (isEdit) {
                await axiosInstance.put(`/api/finance/credit-memos/${memoId}`, payload);
                setSuccess('Credit memo updated successfully');
            } else {
                const res = await axiosInstance.post('/api/finance/credit-memos', payload);
                if (res.data.success && res.data.data?.id) {
                    navigate(`/finance-portal/credit-memos/${res.data.data.id}`, { replace: true });
                    return;
                }
                setSuccess('Credit memo created successfully');
            }
        } catch (err) {
            const serverErrors = err.response?.data?.errors;
            const msg = err.response?.data?.message || 'Failed to save credit memo';
            if (serverErrors && typeof serverErrors === 'object') {
                setErrors(serverErrors);
                setError(msg);
            } else {
                setError(msg);
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: ACCENT }} />
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconButton onClick={() => navigate('/finance-portal/credit-memos')} sx={{ color: 'white', mr: 0.5 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CreditScoreIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>
                            {isEdit ? 'Edit Credit Memo' : 'New Credit Memo'}
                        </Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                            {isEdit ? 'Update credit memo details' : 'Create a new credit memo'}
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1200, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Credit Memo Details */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2 }}>Credit Memo Details</Typography>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                            <Autocomplete
                                size="small"
                                options={customerOptions}
                                getOptionLabel={opt => opt ? (opt.company_name || `${opt.first_name || ''} ${opt.last_name || ''}`.trim()) : ''}
                                value={selectedCustomer}
                                onChange={(_, val) => setSelectedCustomer(val)}
                                onInputChange={(_, val) => setCustomerSearch(val)}
                                loading={customerLoading}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Customer"
                                        required
                                        InputLabelProps={{ ...params.InputLabelProps, sx: { fontFamily } }}
                                        InputProps={{
                                            ...params.InputProps,
                                            sx: { fontFamily, borderRadius: '10px' },
                                            endAdornment: (
                                                <>
                                                    {customerLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                noOptionsText="Type to search customers"
                            />
                            <Autocomplete
                                size="small"
                                options={invoiceOptions}
                                getOptionLabel={opt => opt ? `${opt.invoice_number || 'INV-???'} - ${formatCurrency(opt.total)}` : ''}
                                value={selectedInvoice}
                                onChange={(_, val) => handleInvoiceSelect(val)}
                                onInputChange={(_, val) => setInvoiceSearch(val)}
                                loading={invoiceLoading}
                                disabled={!selectedCustomer}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Original Invoice"
                                        placeholder={selectedCustomer ? 'Search invoices...' : 'Select a customer first'}
                                        InputLabelProps={{ ...params.InputLabelProps, sx: { fontFamily } }}
                                        InputProps={{
                                            ...params.InputProps,
                                            sx: { fontFamily, borderRadius: '10px' },
                                            endAdornment: (
                                                <>
                                                    {invoiceLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                noOptionsText={selectedCustomer ? 'No invoices found' : 'Select a customer first'}
                            />
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                            <TextField
                                size="small" type="date" label="Credit Date" required
                                value={creditDate}
                                onChange={e => setCreditDate(e.target.value)}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            />
                            <FormControl size="small">
                                <InputLabel sx={{ fontFamily }}>Refund Method</InputLabel>
                                <Select
                                    value={refundMethod}
                                    onChange={e => setRefundMethod(e.target.value)}
                                    label="Refund Method"
                                    sx={{ fontFamily, borderRadius: '10px' }}
                                >
                                    {REFUND_METHODS.map(rm => (
                                        <MenuItem key={rm.value} value={rm.value} sx={{ fontFamily }}>{rm.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <TextField
                            size="small"
                            label="Reason"
                            fullWidth
                            multiline
                            rows={2}
                            placeholder="Reason for this credit memo"
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            InputLabelProps={{ sx: { fontFamily } }}
                            InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        />
                    </CardContent>
                </Card>

                {/* Line Items */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>Line Items</Typography>
                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={addLine}
                                sx={{ fontFamily, textTransform: 'none', color: ACCENT, fontWeight: 600 }}
                            >
                                Add Line Item
                            </Button>
                        </Box>

                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase' }}>Description</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 90 }}>Qty</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 120 }}>Unit Price</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 120 }}>Tax Amount</TableCell>
                                        <TableCell align="right" sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 120 }}>Amount</TableCell>
                                        <TableCell sx={{ width: 50 }} />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {lines.map((line, index) => {
                                        const amt = lineAmount(line);
                                        return (
                                            <TableRow key={index} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                                                <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        multiline
                                                        maxRows={3}
                                                        placeholder="Description"
                                                        value={line.description}
                                                        onChange={e => updateLine(index, 'description', e.target.value)}
                                                        InputProps={{ sx: { fontFamily, fontSize: '0.85rem', borderRadius: '8px' } }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        placeholder="1"
                                                        value={line.quantity}
                                                        onChange={e => updateLine(index, 'quantity', e.target.value)}
                                                        inputProps={{ min: 0, step: 'any' }}
                                                        InputProps={{ sx: { fontFamily, fontSize: '0.85rem', borderRadius: '8px' } }}
                                                        sx={{ width: '100%' }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        placeholder="0.00"
                                                        value={line.unit_price}
                                                        onChange={e => updateLine(index, 'unit_price', e.target.value)}
                                                        inputProps={{ min: 0, step: '0.01' }}
                                                        InputProps={{ sx: { fontFamily, fontSize: '0.85rem', borderRadius: '8px' } }}
                                                        sx={{ width: '100%' }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        placeholder="0.00"
                                                        value={line.tax_amount}
                                                        onChange={e => updateLine(index, 'tax_amount', e.target.value)}
                                                        inputProps={{ min: 0, step: '0.01' }}
                                                        InputProps={{ sx: { fontFamily, fontSize: '0.85rem', borderRadius: '8px' } }}
                                                        sx={{ width: '100%' }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right" sx={{ verticalAlign: 'top', pt: 1.5 }}>
                                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                                                        {formatCurrency(amt)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => removeLine(index)}
                                                        disabled={lines.length <= 1}
                                                        sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={addLine}
                            sx={{ fontFamily, textTransform: 'none', color: ACCENT, mt: 1.5, fontWeight: 600 }}
                        >
                            Add Line Item
                        </Button>
                    </CardContent>
                </Card>

                {/* Totals & Notes */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
                    {/* Notes */}
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2 }}>Notes</Typography>
                            <TextField
                                label="Notes"
                                size="small"
                                fullWidth
                                multiline
                                rows={4}
                                placeholder="Internal notes for this credit memo"
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                                InputLabelProps={{ sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            />
                        </CardContent>
                    </Card>

                    {/* Totals */}
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2 }}>Summary</Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b' }}>Subtotal</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>{formatCurrency(subtotal)}</Typography>
                                </Box>
                                {totalTax > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b' }}>Tax</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>{formatCurrency(totalTax)}</Typography>
                                    </Box>
                                )}
                                <Divider />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5 }}>
                                    <Typography sx={{ fontFamily, fontSize: '1.1rem', fontWeight: 700, color: '#1e293b' }}>Total</Typography>
                                    <Typography sx={{ fontFamily, fontSize: '1.1rem', fontWeight: 700, color: ACCENT }}>{formatCurrency(total)}</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <Button
                        onClick={() => navigate('/finance-portal/credit-memos')}
                        sx={{ fontFamily, textTransform: 'none', color: '#64748b', fontWeight: 600 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        onClick={handleSaveDraft}
                        disabled={saving || !selectedCustomer}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            borderColor: ACCENT, color: ACCENT,
                            '&:hover': { borderColor: '#16a34a', bgcolor: 'rgba(34,197,94,0.04)' },
                        }}
                    >
                        {saving ? 'Saving...' : 'Save Draft'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
